// Application.js
import React, { useState, useCallback, useEffect, memo } from "react";
import { createRoot } from "react-dom/client";
import { createPortal } from "react-dom";

// Global handlers for dialog control
let pushDialog = null;
let popDialog = null;

// Store root instances for mounted components
const mountedRoots = new Map();

// Import components dynamically to prevent import issues
const importComponent = (componentName) => {
    try {
        // Create a safe registry of components
        const components = {
            "email": React.lazy(() => import("./Email")),
            "write": React.lazy(() => import("./Write")),
            "viewer": React.lazy(() => import("./Viewer")),
            "login": React.lazy(() => import("./Login")),
            // Add more components as needed
        };

        return components[componentName] || null;
    } catch (error) {
        console.error(`Failed to load component ${componentName}:`, error);
        return null;
    }
};

// Dialog component for displaying modal content
const Dialog = memo(({ component, onClose, zIndex }) => {
    // Event handler for closing dialog with escape key
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                onClose?.();
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        // Clean up event listener
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [onClose]);

    // Prevent dialog from closing when clicking inside the dialog
    const handleDialogClick = useCallback((e) => {
        e.stopPropagation();
    }, []);

    // Safely load component
    const Component = importComponent(component.componentName);

    if (!Component) {
        console.warn(`Component "${component.componentName}" not found in registry.`);
        return null;
    }

    return createPortal(
        <div
            /*onClick={onClose}*/
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "var(--theme)",
                display: "flex",
                justifyContent: "space-evenly",
                alignItems: "center",
                zIndex: zIndex || 1000,
            }}
            role="dialog"
            aria-modal="true"
        >
            <div
                className={`card animated slide ${component.size}`}
                onClick={handleDialogClick}
            >
                <div>
                    <button
                        onClick={onClose}
                        style={{ float: "right" }}
                        aria-label="Close modal"
                        type="button"
                    >
                        ×
                    </button>
                    {component.title && <h3>{component.title}</h3>}
                </div>
                <React.Suspense fallback={<div>Loading...</div>}>
                    <Component {...component.props} />
                </React.Suspense>
            </div>
        </div>,
        document.body
    );
});

Dialog.displayName = "Dialog";

// Application component that manages dialog stack
function Application() {
    const [dialogs, setDialogs] = useState([]);

    // Push and pop dialog helpers
    const openDialogHandler = useCallback((dialog) => {
        setDialogs((prev) => [...prev, dialog]);
    }, []);

    const closeTopDialog = useCallback(() => {
        setDialogs((prev) => prev.slice(0, -1));
    }, []);

    const closeSpecificDialog = useCallback((id) => {
        setDialogs((prev) => prev.filter((d) => d.id !== id));
    }, []);

    // Expose dialog control functions globally
    useEffect(() => {
        pushDialog = openDialogHandler;
        popDialog = closeTopDialog;
        return () => {
            pushDialog = null;
            popDialog = null;
        };
    }, [openDialogHandler, closeTopDialog]);

    return (
        <div>
            {dialogs.map((dialog, index) => (
                <Dialog
                    key={dialog.id}
                    component={dialog}
                    onClose={() => closeSpecificDialog(dialog.id)}
                    zIndex={1000 + index} // Stack dialogs visually
                />
            ))}
        </div>
    );
}

// Mount function for rendering components to the DOM
export const mountComponent = (targetId, Component, props = {}) => {
    try {
        const element = document.getElementById(targetId);
        if (element) {
            const root = createRoot(element);
            mountedRoots.set(targetId, root); // Store the root instance
            root.render(
                <React.StrictMode>
                    <Component {...props} />
                </React.StrictMode>
            );
            return true;
        }
        console.warn(`Target element with ID "${targetId}" not found for mounting.`);
        return false;
    } catch (error) {
        console.error(`Failed to mount component to #${targetId}:`, error);
        return false;
    }
};

/**
 * Unmounts a React component from a specific DOM element.
 * @param {string} targetId The ID of the DOM element where the component was mounted.
 * @returns {boolean} True if the component was successfully unmounted, false otherwise.
 */
export const unmountComponent = (targetId) => {
    try {
        const root = mountedRoots.get(targetId);
        if (root) {
            root.unmount();
            mountedRoots.delete(targetId); // Remove the root instance from the map
            return true;
        }
        console.warn(`No component found mounted on #${targetId} to unmount.`);
        return false;
    } catch (error) {
        console.error(`Failed to unmount component from #${targetId}:`, error);
        return false;
    }
};

export const mountDialog = (component, props = {}, title = "", size = "small") => {
    try {
        const element = document.getElementById(component);
        if (element) {
            element.addEventListener('click', (e) => {
                e.preventDefault();
                openDialog(component, props, title, size);
            });
            return true;
        }
        return false;
    } catch (error) {
        console.error(`Failed to mount component to #${component}:`, error);
        return false;
    }
};

// Open a new dialog (adds to stack)
export const openDialog = (componentName, props = {}, title = "", size = "small", callback = null) => {
    if (typeof pushDialog === "function") {
        pushDialog({ id: crypto.randomUUID(), componentName, props, title, size, callback });
    } else {
        console.warn("Dialog system not initialized yet.");
    }
};

// Close the topmost dialog
export const closeDialog = () => {
    if (typeof popDialog === "function") {
        popDialog();
    }
};

export default Application;